"use client";

import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useGrns } from "@/hooks/api/useGrns";
import { usePurchaseOrders } from "@/hooks/api/usePurchaseOrders";
import { useSupplier, useSupplierLedger } from "@/hooks/api/useSuppliers";
import { Card } from "@/components/ui/card";

type LedgerRow = {
  id: number;
  payment_date: string;
  amount: number;
  payment_mode: string;
  reference_no?: string | null;
  bill_no?: string;
};

export default function SupplierDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const { data: supplier, isLoading: ls } = useSupplier(Number.isFinite(id) ? id : null);
  const { data: ledger = [], isLoading: ll } = useSupplierLedger(Number.isFinite(id) ? id : null);
  const { data: pos = [] } = usePurchaseOrders({ supplier_id: Number.isFinite(id) ? id : undefined });
  const { data: grns = [] } = useGrns({ supplier_id: Number.isFinite(id) ? id : undefined });

  if (!Number.isFinite(id)) {
    return <p className="p-4 text-sm text-destructive">Invalid id</p>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <Link to="/suppliers" className="text-sm text-muted-foreground hover:underline">
        ← Suppliers
      </Link>
      {ls && <p className="text-sm text-muted-foreground">Loading…</p>}
      {supplier && (
        <>
          <div>
            <h1 className="text-2xl font-semibold">{supplier.name}</h1>
            <p className="text-sm text-muted-foreground">
              {supplier.phone} · {supplier.email} · GSTIN {supplier.gstin ?? "—"}
            </p>
            <p className="mt-2 text-sm">
              Outstanding: <strong>₹{Number(supplier.outstanding_balance ?? 0).toLocaleString("en-IN")}</strong>
              {" · "}
              Credit limit: ₹{Number(supplier.credit_limit ?? 0).toLocaleString("en-IN")}
            </p>
          </div>

          <Card className="p-4">
            <h2 className="mb-3 font-medium">Payment ledger</h2>
            {ll && <p className="text-xs text-muted-foreground">Loading…</p>}
            <div className="space-y-2">
              {(ledger as LedgerRow[]).map((row) => (
                <div
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-l-2 border-primary/40 pl-3 text-sm"
                >
                  <span className="text-muted-foreground">{row.payment_date}</span>
                  <span className="font-mono">
                    ₹{Number(row.amount).toLocaleString("en-IN")} ({row.payment_mode})
                  </span>
                  <span className="text-xs text-muted-foreground">{row.bill_no}</span>
                </div>
              ))}
              {ledger.length === 0 && !ll && <p className="text-xs text-muted-foreground">No payments yet.</p>}
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="mb-2 font-medium">Purchase orders</h2>
            <ul className="list-inside list-disc text-sm">
              {pos.map((p) => (
                <li key={p.id}>
                  <Link className="font-mono hover:underline" to={`/purchase/orders/${p.id}`}>
                    {p.po_no}
                  </Link>{" "}
                  — {p.status}
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-4">
            <h2 className="mb-2 font-medium">GRNs</h2>
            <ul className="list-inside list-disc text-sm">
              {grns.map((g) => (
                <li key={g.id}>
                  <Link className="font-mono hover:underline" to={`/purchase/grn/${g.id}`}>
                    {g.grn_no}
                  </Link>{" "}
                  — {g.status}
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}
